import { cs } from '../../lib/currency';
import { RETIRED_TOOLS_VISIBLE } from '../../lib/retiredTools';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Loader2,
  Download,
  TrendingUp,
  TrendingDown,
  Scale,
  Hash,
  Columns3,
  ChevronUp,
  ChevronDown,
  RotateCcw,
  Wrench,
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';
import { DateRangePicker, toQueryParams } from './billing/DateRangePicker';
import {
  formatPaymentMethod,
  PAYMENT_METHOD_FILTER_OPTIONS,
} from '../../components/admin/invoice/payment_method_helpers';
import { useBusinessTimezone } from '../../utils/businessTime';
import SortableTable from '../../components/ui/sortableTable/SortableTable';
import { useFeatures } from '../../context/FeatureContext';

// iter302: use `toFixed(2)` for the integer + decimal half (deterministic
// — no locale variability, no Intl polyfill quirks). Add thousands
// separators by hand so we still get $1,234.56 readability. Replaces
// the iter290 toLocaleString-based formatter which appeared to render
// `$140.000` on a small subset of production rows (suspected
// build/cache artefact; rock-solid here so it never recurs).
const formatMoney = (v) => {
  const n = Number(v);
  const safe = Number.isFinite(n) ? n : 0;
  const [whole, dec] = safe.toFixed(2).split('.');
  const sign = whole.startsWith('-') ? '-' : '';
  const abs = sign ? whole.slice(1) : whole;
  const withSep = abs.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${cs()}${withSep}.${dec}`;
};

// iter309-hotfix (Feb 2026): defensive normalizer for the server-formatted
// `amount_display` string. Production reported `+$300.000` (3-decimal
// values) on some rows despite both server and client using `.2f` /
// `.toFixed(2)` — root cause was either a stale PWA bundle serving an
// older `toLocaleString`-based formatter or a legacy row whose stored
// value contained trailing zeros the formatter didn't strip. This helper
// is idempotent: if `s` already looks like `[-]$X,XXX.XX` it's returned
// unchanged; otherwise we re-parse and reformat through `formatMoney` so
// the ledger renders exactly 2 decimals no matter what.
const normalizeMoneyString = (s) => {
  if (typeof s !== 'string' || !s) return s;
  // Fast-path: strictly matches "-$1,234.56" / "$0.00" / "-$0.00" with
  // exactly 2 decimals.
  if (/^-?\$-?\d{1,3}(,\d{3})*\.\d{2}$/.test(s) || /^-?\$-?\d+\.\d{2}$/.test(s)) {
    return s;
  }
  const m = s.match(/^(-?)\$?(-?)([\d,]+)(?:\.(\d+))?$/);
  if (!m) return s;
  const sign = m[1] || m[2] || '';
  const whole = m[3].replace(/,/g, '');
  const dec = m[4] || '0';
  const n = Number(`${whole}.${dec}`);
  if (!Number.isFinite(n)) return s;
  return formatMoney(sign === '-' ? -n : n);
};

const formatDate = (iso, tz) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      timeZone: tz || 'America/New_York',
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

/* ------------------------------------------------------------------ */
/*  Link helpers                                                       */
/* ------------------------------------------------------------------ */

const entityHref = (row) => {
  const { entity_type, source, customer_name, customer_email } = row;
  if (source === 'refund') return '/admin/refunds';

  // iter321c — All description links now route to /admin/invoice-management
  // filtered by (entity type) + (customer name/email when available). This
  // gives admins the FULL invoice picture for that customer instead of the
  // tunnel-vision enrollment-only / 404-prone deep links we shipped before.
  //
  // Customer filter is a string search across parent_name/parent_email so we
  // pass whichever identifier we have. Falls back to type-only filtering
  // when the row has no customer (orphan/system payments).
  const params = new URLSearchParams();
  if (entity_type) params.set('type', entity_type);
  // Show ALL statuses (not just overdue) so admins see paid invoices too.
  params.set('status', 'all');
  const customer = customer_name || customer_email;
  if (customer) params.set('customer', customer);

  return `/admin/invoice-management?${params.toString()}`;
};

/* ------------------------------------------------------------------ */
/*  Entity badge                                                       */
/* ------------------------------------------------------------------ */

const ENTITY_BADGE_META = {
  enrollment:   { label: 'Enrollment',   tone: 'text-indigo-700 border-indigo-200 bg-indigo-50' },
  coaching:     { label: 'Coaching',     tone: 'text-indigo-700 border-indigo-200 bg-indigo-50' },
  subscription: { label: 'Subscription', tone: 'text-indigo-700 border-indigo-200 bg-indigo-50' },
  lane_booking: { label: 'Lane',         tone: 'text-sky-700 border-sky-200 bg-sky-50' },
  small_group:  { label: 'Small group',  tone: 'text-violet-700 border-violet-200 bg-violet-50' },
  tournament:   { label: 'Tournament',   tone: 'text-amber-700 border-amber-200 bg-amber-50' },
  camp:         { label: 'Camp',         tone: 'text-orange-700 border-orange-200 bg-orange-50' },
  refund:       { label: 'Refund',       tone: 'text-rose-700 border-rose-200 bg-rose-50' },
};
const entityBadge = (entity_type) => {
  const key = (entity_type || '').toLowerCase();
  return ENTITY_BADGE_META[key] || { label: entity_type || '—', tone: 'text-slate-600 border-slate-200 bg-slate-50' };
};

/* ------------------------------------------------------------------ */
/*  Column definitions                                                 */
/* ------------------------------------------------------------------ */

const COLUMN_DEFS = {
  date:          { label: 'Transaction Date', sortable: true,  width: 'w-44' },
  activity_date: { label: 'Activity Date',    sortable: false, width: 'w-32' },
  type:          { label: 'Type',             sortable: true,  width: 'w-24' },
  entity_type:   { label: 'Entity',           sortable: true,  width: 'w-32' },
  description:   { label: 'Description',      sortable: true,  width: '' },
  customer_name: { label: 'Customer',         sortable: true,  width: '' },
  method:        { label: 'Method',           sortable: true,  width: 'w-28' },
  amount:        { label: 'Amount',           sortable: true,  width: 'w-32', align: 'right' },
};
const DEFAULT_ORDER = ['date', 'activity_date', 'type', 'entity_type', 'description', 'customer_name', 'method', 'amount'];
const LS_KEY = 'admin.transactions.prefs.v2'; // v2 = activity_date column added

const DEFAULT_PREFS = {
  order: DEFAULT_ORDER,
  visible: DEFAULT_ORDER.reduce((a, k) => ({ ...a, [k]: true }), {}),
  sortBy: 'date',
  sortDir: 'desc',
};

const loadPrefs = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const p = JSON.parse(raw);
    // Drop keys that no longer exist; add any new keys as visible.
    const validKeys = Object.keys(COLUMN_DEFS);
    const order = (p.order || []).filter((k) => validKeys.includes(k));
    for (const k of validKeys) if (!order.includes(k)) order.push(k);
    const visible = { ...DEFAULT_PREFS.visible, ...(p.visible || {}) };
    return {
      order,
      visible,
      sortBy: p.sortBy || 'date',
      sortDir: p.sortDir || 'desc',
    };
  } catch { return DEFAULT_PREFS; }
};

const savePrefs = (prefs) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(prefs)); } catch { /* ignore */ }
};

/* ------------------------------------------------------------------ */
/*  Cell renderers                                                     */
/* ------------------------------------------------------------------ */

const renderCell = (colKey, row, { goToCustomer, goToEntity, businessTz }) => {
  const isCredit = row.type === 'credit';
  switch (colKey) {
    case 'date':
      return <span className="font-mono text-xs text-slate-600">{formatDate(row.date, businessTz)}</span>;
    case 'activity_date': {
      if (!row.activity_date) {
        return <span className="text-slate-400 text-xs">—</span>;
      }
      // Strip time component if it leaked in — activity dates are date-only.
      const d = String(row.activity_date).slice(0, 10);
      return (
        <span
          className="font-mono text-xs text-slate-700 inline-flex items-center gap-1"
          data-testid={`txn-activity-date-${row.id}`}
          title={row.activity_date_is_inherited
            ? 'Inherited from the original entity (refund)'
            : 'Date the service was delivered'}
        >
          {d}
          {row.activity_date_is_inherited && (
            <span className="text-slate-400" aria-label="Inherited">↻</span>
          )}
        </span>
      );
    }
    case 'type': {
      const TypeIcon = isCredit ? ArrowDownCircle : ArrowUpCircle;
      return (
        <Badge
          variant="outline"
          className={isCredit
            ? 'text-emerald-700 border-emerald-200 bg-emerald-50'
            : 'text-rose-700 border-rose-200 bg-rose-50'}
        >
          <TypeIcon className="h-3 w-3 mr-1" />
          {isCredit ? 'Credit' : 'Debit'}
        </Badge>
      );
    }
    case 'entity_type': {
      const ent = entityBadge(row.entity_type);
      return (
        <Badge variant="outline" className={ent.tone} data-testid={`txn-entity-${row.id}`}>
          {ent.label}
        </Badge>
      );
    }
    case 'description':
      return (
        <button
          className="text-left text-sky-700 hover:underline"
          onClick={() => goToEntity(row)}
          data-testid={`txn-description-${row.id}`}
          title="Open linked invoice / entity"
        >
          {row.description || '—'}
        </button>
      );
    case 'customer_name':
      return row.customer_id ? (
        <button
          className="text-left text-sky-700 hover:underline"
          onClick={() => goToCustomer(row.customer_id, row.customer_is_parent)}
          data-testid={`txn-customer-${row.id}`}
          title={row.customer_is_parent ? 'Open parent profile' : 'Open customer profile'}
        >
          {row.customer_name || row.customer_email || row.customer_id.slice(0, 8)}
        </button>
      ) : <span className="text-slate-400">—</span>;
    case 'method': {
      const m = formatPaymentMethod(row.method);
      if (!m) return <span className="text-xs text-slate-400">—</span>;
      return (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${m.className}`}
          data-testid={`txn-method-${row.id}`}
        >
          {m.label}
        </span>
      );
    }
    case 'amount':
      return (
        <button
          className={isCredit ? 'text-emerald-700 hover:underline font-semibold' : 'text-rose-700 hover:underline font-semibold'}
          onClick={() => goToEntity(row)}
          data-testid={`txn-amount-${row.id}`}
          title={
            row.surcharge_amount && row.surcharge_amount > 0
              ? `Base ${cs()}${Number(row.base_amount || 0).toFixed(2)} + CC surcharge ${cs()}${Number(row.surcharge_amount).toFixed(2)}`
              : 'Open linked invoice / entity'
          }
        >
          {/* iter303: prefer the server-formatted amount string if
               present — bulletproof against any stale-bundle/locale
               weirdness on the client. Falls back to local formatMoney.
               iter309-hotfix: pass server string through
               `normalizeMoneyString` so any legacy 3-decimal payload
               (`$300.000`) is coerced to exactly 2 decimals. */}
          {(() => {
            const raw = row.amount_display ? normalizeMoneyString(row.amount_display) : null;
            if (raw) {
              const signed = raw.startsWith('-') ? raw : (isCredit ? `+${raw}` : `−${raw}`);
              return signed;
            }
            return `${isCredit ? '+' : '−'}${formatMoney(row.amount)}`;
          })()}
          {(() => {
            // iter309-hotfix: on legacy rows the DB occasionally stores
            // `surcharge_amount` in cents (e.g. `165` for a $1.65 fee).
            // The subline shown to admins was misleading ($165 CC fee
            // on a $56.65 total). Suppress the subline when the
            // surcharge is >= the total amount — obviously bad data,
            // and log a console warning so we can investigate the row.
            const sc = Number(row.surcharge_amount);
            const amt = Number(row.amount);
            if (!(sc > 0.005)) return null;
            if (Number.isFinite(sc) && Number.isFinite(amt) && sc >= amt - 0.005) {
              if (typeof console !== 'undefined') {
                console.warn(
                  '[transactions] suppressed impossible surcharge_amount on row',
                  { id: row.id, amount: amt, surcharge_amount: sc },
                );
              }
              return null;
            }
            const scStr = row.surcharge_amount_display
              ? normalizeMoneyString(row.surcharge_amount_display)
              : `${cs()}${sc.toFixed(2)}`;
            return (
              <span
                className="block text-[10px] font-normal text-amber-600 leading-tight"
                data-testid={`txn-amount-${row.id}-breakdown`}
              >
                {`incl. ${scStr} CC fee`}
              </span>
            );
          })()}
        </button>
      );
    default:
      return null;
  }
};

/* ------------------------------------------------------------------ */
/*  Column picker                                                      */
/* ------------------------------------------------------------------ */

const ColumnPicker = ({ order, visible, onChange, onReset }) => {
  const toggleVisible = (key) => {
    const next = { ...visible, [key]: !visible[key] };
    // Never let admins hide ALL columns — always keep at least one.
    if (!Object.values(next).some(Boolean)) return;
    onChange({ order, visible: next });
  };
  const move = (idx, delta) => {
    const ni = idx + delta;
    if (ni < 0 || ni >= order.length) return;
    const next = [...order];
    [next[idx], next[ni]] = [next[ni], next[idx]];
    onChange({ order: next, visible });
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="columns-picker-btn">
          <Columns3 className="h-4 w-4 mr-1.5" /> Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="end" data-testid="columns-picker">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium">Columns</p>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7"
            onClick={onReset}
            data-testid="columns-reset-btn"
          >
            <RotateCcw className="h-3 w-3 mr-1" /> Reset
          </Button>
        </div>
        <div className="space-y-1">
          {order.map((key, idx) => (
            <div
              key={key}
              className="flex items-center justify-between gap-2 py-1 px-2 rounded hover:bg-slate-50"
              data-testid={`col-row-${key}`}
            >
              <label className="flex items-center gap-2 cursor-pointer flex-1">
                <Checkbox
                  checked={!!visible[key]}
                  onCheckedChange={() => toggleVisible(key)}
                  data-testid={`col-toggle-${key}`}
                />
                <span className="text-sm">{COLUMN_DEFS[key]?.label || key}</span>
              </label>
              <div className="flex gap-0.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  data-testid={`col-up-${key}`}
                  aria-label={`Move ${key} up`}
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => move(idx, 1)}
                  disabled={idx === order.length - 1}
                  data-testid={`col-down-${key}`}
                  aria-label={`Move ${key} down`}
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const TYPE_OPTIONS = [
  { value: 'all',    label: 'All transactions' },
  { value: 'credit', label: 'Credits only' },
  { value: 'debit',  label: 'Debits only' },
];

const ENTITY_OPTIONS = [
  { value: 'all',          label: 'All entities' },
  { value: 'enrollment',   label: 'Enrollment',   featureKey: 'enrollments.core' },
  { value: 'lane_booking', label: 'Lane booking',  featureKey: 'bookings.lanes' },
  { value: 'small_group',  label: 'Small group',   featureKey: 'bookings.small_groups' },
  { value: 'tournament',   label: 'Tournament',    featureKey: 'tournaments.registrations' },
  { value: 'camp',         label: 'Camp',          featureKey: 'camps.core' },
  { value: 'coaching',     label: 'Coaching' },
  { value: 'refund',       label: 'Refund' },
];

const PAGE_SIZE = 50;

export default function Transactions() {
  const { isPlanFeatureEnabled } = useFeatures();
  const navigate = useNavigate();
  const businessTz = useBusinessTimezone();

  const [dateRange, setDateRange] = useState({ period: 'current_month' });
  const [typeFilter, setTypeFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [locations, setLocations] = useState([]);
  // iter306aa: when on, hide parent-side account_credit adjustments so the
  // ledger only shows real academy cash flow.
  const [academyOnly, setAcademyOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs);

  // Persist prefs whenever they change.
  useEffect(() => { savePrefs(prefs); }, [prefs]);

  // Load business locations for the location filter.
  useEffect(() => {
    api.get('/locations/active').then(r => setLocations(r.data || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const qp = toQueryParams(dateRange);
      if (typeFilter !== 'all') qp.append('type', typeFilter);
      if (entityFilter !== 'all') qp.append('entity_type', entityFilter);
      if (methodFilter !== 'all') qp.append('method', methodFilter);
      if (locationFilter !== 'all') qp.append('location_id', locationFilter);
      if (academyOnly) qp.append('academy_only', 'true');
      if (prefs.sortBy) {
        qp.append('sort_by', prefs.sortBy);
        qp.append('sort_dir', prefs.sortDir || 'desc');
      }
      qp.append('page', String(page));
      qp.append('limit', String(PAGE_SIZE));
      const res = await api.get(`/admin/transactions?${qp.toString()}`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [dateRange, typeFilter, entityFilter, methodFilter, locationFilter, academyOnly, page, prefs.sortBy, prefs.sortDir]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [dateRange, typeFilter, entityFilter, methodFilter, locationFilter, academyOnly, prefs.sortBy, prefs.sortDir]);

  const transactions = data?.transactions || [];
  const summary = data?.summary || { total_credits: 0, total_debits: 0, net: 0, count: 0 };
  const total = data?.total || 0;

  const goToCustomer = (customerId, isParent) => {
    if (!customerId) return;
    // iter309-hotfix: route to parent profile if this customer has any
    // children on file (matches InvoiceManagement behaviour); otherwise
    // fall back to the standalone customer profile.
    if (isParent) {
      navigate(`/admin/parents/${customerId}?tab=billing`);
    } else {
      navigate(`/admin/customers/${customerId}`);
    }
  };
  const goToEntity = (row) => { const href = entityHref(row); if (href) navigate(href); };

  /* -------- Sort header click — delegated to SortableTable via external control -------- */
  const sortStateForTable = useMemo(() => ({
    field: prefs.sortBy || null,
    direction: prefs.sortDir || 'asc',
  }), [prefs.sortBy, prefs.sortDir]);

  const handleSortChange = (next) => {
    // SortableTable already cycles asc → desc → null. Mirror that into prefs.
    setPrefs((prev) => ({
      ...prev,
      sortBy: next.field || null,
      sortDir: next.field ? next.direction : null,
    }));
  };

  /* -------- CSV export respects visible columns + order -------- */
  const visibleOrderedKeys = useMemo(
    () => prefs.order.filter((k) => prefs.visible[k]),
    [prefs.order, prefs.visible],
  );

  const downloadCsv = () => {
    if (!transactions.length) { toast.info('No transactions to export'); return; }
    const header = visibleOrderedKeys.map((k) => COLUMN_DEFS[k]?.label || k);
    const rowFor = (row, k) => {
      switch (k) {
        case 'date':          return row.date || '';
        case 'type':          return row.type;
        case 'entity_type':   return entityBadge(row.entity_type).label;
        case 'description':   return row.description || '';
        case 'customer_name': return row.customer_name || row.customer_email || '';
        case 'method':        return formatPaymentMethod(row.method)?.label || row.method || '';
        case 'amount':        return `${row.type === 'credit' ? '' : '-'}${row.amount}`;
        default:              return '';
      }
    };
    const rows = transactions.map((r) => visibleOrderedKeys.map((k) => rowFor(r, k)));
    const csv = [header, ...rows]
      .map((arr) => arr.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${data?.start_date?.slice(0, 10) || 'all'}_${data?.end_date?.slice(0, 10) || 'now'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const summaryCards = useMemo(() => ([
    { label: 'Credits (money in)', value: normalizeMoneyString(summary.total_credits_display) || formatMoney(summary.total_credits), icon: TrendingUp,   tone: 'text-emerald-700 bg-emerald-50 border-emerald-200', testid: 'summary-credits' },
    { label: 'Debits (refunds out)', value: normalizeMoneyString(summary.total_debits_display) || formatMoney(summary.total_debits), icon: TrendingDown, tone: 'text-rose-700 bg-rose-50 border-rose-200',          testid: 'summary-debits' },
    { label: 'Net',               value: normalizeMoneyString(summary.net_display) || formatMoney(summary.net),           icon: Scale,        tone: summary.net >= 0 ? 'text-slate-800 bg-slate-50 border-slate-200' : 'text-rose-700 bg-rose-50 border-rose-200', testid: 'summary-net' },
    { label: 'Transactions',      value: String(summary.count || 0),         icon: Hash,         tone: 'text-slate-700 bg-white border-slate-200',           testid: 'summary-count' },
  ]), [summary]);

  return (
    <DashboardLayout title="Transactions">
      <div className="space-y-5" data-testid="transactions-page">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-slate-600 mr-2">Every inflow and outflow across Stripe, manual payments and refunds</p>
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-48" data-testid="transaction-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`type-filter-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-48" data-testid="transaction-entity-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENTITY_OPTIONS.filter((o) => !o.featureKey || !isPlanFeatureEnabled || isPlanFeatureEnabled(o.featureKey)).map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`entity-filter-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={methodFilter} onValueChange={setMethodFilter}>
            <SelectTrigger className="w-48" data-testid="transaction-method-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAYMENT_METHOD_FILTER_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`method-filter-${o.value}`}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {locations.length > 0 && (
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-48" data-testid="transaction-location-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="location-filter-all">All locations</SelectItem>
                {locations.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id} data-testid={`location-filter-${loc.id}`}>
                    {loc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* iter306aa: Academy P&L-only toggle. Hides parent account_credit
              adjustments + audit-only rows so the ledger reflects only
              real money in/out for academy accounting. */}
          <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded border border-slate-200 bg-white cursor-pointer hover:bg-slate-50">
            <input
              type="checkbox"
              checked={academyOnly}
              onChange={(e) => setAcademyOnly(e.target.checked)}
              className="h-4 w-4"
              data-testid="transactions-academy-only-toggle"
            />
            <span className="text-sm font-medium text-slate-700">Academy P&amp;L only</span>
          </label>
          <div className="ml-auto flex items-center gap-2">
            <ColumnPicker
              order={prefs.order}
              visible={prefs.visible}
              onChange={(partial) => setPrefs((p) => ({ ...p, ...partial }))}
              onReset={() => setPrefs({ ...DEFAULT_PREFS, sortBy: prefs.sortBy, sortDir: prefs.sortDir })}
            />
            {RETIRED_TOOLS_VISIBLE && (<Button
              variant="outline"
              size="sm"
              onClick={async () => {
                setBackfilling(true);
                try {
                  const { data: r } = await api.post('/admin/transactions/backfill-stuck');
                  const promoted = r?.stats?.promoted ?? 0;
                  if (promoted > 0) {
                    toast.success(
                      `Repaired ${promoted} stuck transaction${promoted === 1 ? '' : 's'}. Reloading…`,
                    );
                    fetchData();
                  } else {
                    toast.info(
                      `Nothing to repair — examined ${r?.stats?.examined ?? 0} unsettled rows, all linked entities are also unpaid.`,
                    );
                  }
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Failed to repair transactions');
                } finally {
                  setBackfilling(false);
                }
              }}
              disabled={loading || backfilling}
              title="Find paid Stripe payments whose ledger row is stuck and bump them to paid. Idempotent — safe to re-run."
              data-testid="backfill-stuck-btn"
            >
              {backfilling ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Wrench className="h-4 w-4 mr-1.5" />
              )}
              Repair stuck
            </Button>)}
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCsv}
              disabled={loading || transactions.length === 0}
              data-testid="export-csv-btn"
            >
              <Download className="h-4 w-4 mr-1.5" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="transactions-summary">
          {summaryCards.map((c) => {
            const Icon = c.icon;
            return (
              <Card key={c.label} className={`border ${c.tone}`} data-testid={c.testid}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium uppercase tracking-wide opacity-75">{c.label}</div>
                    <div className="text-2xl font-semibold mt-1">{c.value}</div>
                  </div>
                  <Icon className="h-7 w-7 opacity-60" />
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Table — iter307ar: migrated to <SortableTable> with external server-side sort + pagination */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <SortableTable
              data={transactions}
              columns={visibleOrderedKeys.map((k) => {
                const def = COLUMN_DEFS[k] || {};
                return {
                  key: k,
                  label: def.label || k,
                  sortable: !!def.sortable,
                  align: def.align,
                  width: def.width,
                  render: (row) => renderCell(k, row, { goToCustomer, goToEntity, businessTz }),
                };
              })}
              sortState={sortStateForTable}
              onSortChange={handleSortChange}
              currentPage={page}
              totalCount={total}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              getRowKey={(r) => r.id}
              loading={loading}
              emptyMessage="No transactions in this range"
              testidPrefix="transactions"
            />
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
